<template>
  <modal
    name="bank-account-transfer-cancel"
    :width="560"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    :click-to-close="!cancelling"
    @closed="resetCancelForm"
  >
    <form class="modal-form bank-transfer-modal" @submit.prevent="cancelTransfer">
      <div class="modal-form-header">
        <div class="d-flex align-items-center min-width-0">
          <span class="modal-title-icon modal-title-icon-danger" aria-hidden="true">
            <i class="fas fa-ban"></i>
          </span>

          <div class="min-width-0">
            <h4 class="modal-title mb-0">Cancelar traspaso</h4>
            <small class="modal-subtitle text-truncate d-block">
              # {{ transferFolio }}
            </small>
          </div>
        </div>

        <button
          type="button"
          class="close ml-3"
          :disabled="cancelling"
          title="Cerrar"
          aria-label="Cerrar cancelación del traspaso"
          @click="closeCancelModal"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="modal-form-body">
        <div v-if="cancelError" class="alert alert-danger" role="alert">
          <i class="fas fa-exclamation-circle mr-1" aria-hidden="true"></i>
          {{ cancelError }}
        </div>

        <template v-if="cancelForm.transfer">
          <section class="cancel-summary" aria-label="Traspaso que se cancelará">
            <div class="cancel-summary-main">
              <small>Monto del traspaso</small>
              <strong>{{ formatMoney(cancelForm.transfer.amount) }}</strong>
            </div>

            <div class="cancel-summary-date">
              <small>Fecha</small>
              <strong>{{ formatDate(cancelForm.transfer.transfer_date) }}</strong>
            </div>
          </section>

          <section class="compact-route mt-3" aria-label="Cuentas involucradas">
            <div>
              <small>Origen</small>
              <strong :title="accountLabel(cancelForm.transfer.source_account)">
                {{ accountLabel(cancelForm.transfer.source_account) }}
              </strong>
            </div>

            <span aria-hidden="true">
              <i class="fas fa-arrow-right"></i>
            </span>

            <div>
              <small>Destino</small>
              <strong :title="accountLabel(cancelForm.transfer.destination_account)">
                {{ accountLabel(cancelForm.transfer.destination_account) }}
              </strong>
            </div>
          </section>

          <div class="reversal-notice mt-3" role="note">
            <span class="reversal-notice-icon" aria-hidden="true">
              <i class="fas fa-exclamation-triangle"></i>
            </span>
            <div>
              <strong>Se generarán movimientos inversos</strong>
              <p class="mb-0">
                El registro original permanecerá en el historial y los saldos de ambas
                cuentas serán compensados. Esta acción no puede cancelarse nuevamente.
              </p>
            </div>
          </div>

          <div class="form-group mt-3 mb-0">
            <div class="d-flex justify-content-between align-items-end">
              <label for="cancellation-comment" class="required-label mb-1">
                Motivo de cancelación
              </label>
              <small class="character-counter">
                {{ cancelForm.cancellation_comment.length }}/2000
              </small>
            </div>

            <textarea
              id="cancellation-comment"
              v-model.trim="cancelForm.cancellation_comment"
              class="form-control form-control-sm"
              rows="4"
              maxlength="2000"
              required
              :disabled="cancelling || !transferCanBeCancelled"
              aria-describedby="cancellation-comment-help"
              placeholder="Describe brevemente por qué se cancela el traspaso"
            ></textarea>
            <small id="cancellation-comment-help" class="form-text text-muted">
              El motivo quedará visible en el historial del movimiento.
            </small>
          </div>
        </template>

        <div v-else class="modal-state" role="status">
          <span class="modal-state-icon" aria-hidden="true">
            <i class="fas fa-search"></i>
          </span>
          <strong>Traspaso no disponible</strong>
          <small>No fue posible identificar el registro que deseas cancelar.</small>
        </div>
      </div>

      <div class="modal-form-footer">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="cancelling"
          @click="closeCancelModal"
        >
          Regresar
        </button>

        <button
          v-can="'cancel_bank_account_transfers'"
          type="submit"
          class="btn btn-danger btn-sm"
          :disabled="!canCancelTransfer"
        >
          <i
            :class="cancelling ? 'fas fa-spinner fa-spin' : 'fas fa-ban'"
            class="mr-1"
            aria-hidden="true"
          ></i>
          {{ cancelling ? 'Cancelando...' : 'Confirmar cancelación' }}
        </button>
      </div>
    </form>
  </modal>
</template>

<script>
import { mapActions } from 'vuex';
import request from '@/services/request';

export default {
  name: 'ModalBankAccountTransferCancel',

  data() {
    return {
      cancelling: false,
      cancelError: '',
      cancelForm: {
        transfer: null,
        cancellation_comment: '',
      },
    };
  },

  computed: {
    transferFolio() {
      return this.cancelForm.transfer && this.cancelForm.transfer.id
        ? this.cancelForm.transfer.id
        : 'Confirma la reversa del movimiento';
    },

    transferCanBeCancelled() {
      return this.cancelForm.transfer !== null
        && this.cancelForm.transfer.status === 'posted';
    },

    canCancelTransfer() {
      return !this.cancelling
        && this.transferCanBeCancelled
        && this.cancelForm.cancellation_comment.trim().length > 0;
    },
  },

  methods: {
    ...mapActions({
      successNotification: 'alert/success',
    }),

    openCancelModal(transfer) {
      this.cancelError = '';
      this.cancelForm = {
        transfer: transfer && typeof transfer === 'object' ? transfer : null,
        cancellation_comment: '',
      };

      if (!this.cancelForm.transfer || !this.cancelForm.transfer.id) {
        this.cancelError = 'No fue posible identificar el traspaso seleccionado.';
      } else if (!this.transferCanBeCancelled) {
        this.cancelError = 'Este traspaso ya no se encuentra disponible para cancelación.';
      }

      this.$modal.show('bank-account-transfer-cancel');
    },

    closeCancelModal() {
      if (!this.cancelling) {
        this.$modal.hide('bank-account-transfer-cancel');
      }
    },

    resetCancelForm() {
      if (this.cancelling) {
        return;
      }

      this.cancelError = '';
      this.cancelForm = {
        transfer: null,
        cancellation_comment: '',
      };
    },

    async cancelTransfer() {
      if (!this.canCancelTransfer) {
        return;
      }

      this.cancelling = true;
      this.cancelError = '';

      try {
        const response = await request.post(
          `/bank-account-transfers/${this.cancelForm.transfer.id}/cancel`,
          {
            cancellation_comment: this.cancelForm.cancellation_comment,
          }
        );
        const body = this.unwrapHttpResponse(response);
        const cancelledTransfer = body && (body.data || body.transfer)
          ? (body.data || body.transfer)
          : body;
        const successMessage = body && body.message
          ? body.message
          : 'El traspaso se canceló correctamente.';

        this.successNotification(successMessage);
        this.$emit('cancelled', cancelledTransfer);
        Event.$emit('event-load-bank-account-transfers');
        this.$modal.hide('bank-account-transfer-cancel');
      } catch (error) {
        this.cancelError = this.getErrorMessage(error);
      } finally {
        this.cancelling = false;
      }
    },

    unwrapHttpResponse(response) {
      if (
        response
        && typeof response.status === 'number'
        && Object.prototype.hasOwnProperty.call(response, 'data')
      ) {
        return response.data;
      }

      return response;
    },

    getErrorMessage(error) {
      const response = error && error.response ? error.response.data : null;
      const body = response || (error && error.data) || null;

      if (body && body.errors && typeof body.errors === 'object') {
        const firstKey = Object.keys(body.errors)[0];
        const firstError = firstKey ? body.errors[firstKey] : null;

        if (Array.isArray(firstError) && firstError.length) {
          return firstError[0];
        }

        if (typeof firstError === 'string') {
          return firstError;
        }
      }

      if (body && body.message) {
        return body.message;
      }

      if (error && error.message) {
        return error.message;
      }

      return 'Ocurrió un error al cancelar el traspaso. Intenta nuevamente.';
    },

    accountEntity(account) {
      if (!account || typeof account !== 'object') {
        return null;
      }

      return account.bank_account
        || account.bankAccount
        || account.banks_accounts
        || account;
    },

    accountLabel(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return 'Cuenta no disponible';
      }

      const bank = entity.bank || entity.banks || entity.bank_data || null;
      const accountId = account.id || entity.id || null;
      const name = entity.name
        || entity.account_name
        || entity.alias
        || (bank && bank.name)
        || (accountId ? `Cuenta #${accountId}` : 'Cuenta no disponible');
      const number = entity.account_number
        || entity.number
        || account.account_number
        || account.number
        || '';

      return [name, number].filter((value) => value).join(' · ');
    },

    formatMoney(value) {
      const amount = Number(value || 0);

      return `$ ${new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number.isFinite(amount) ? amount : 0)}`;
    },

    formatDate(value) {
      if (!value) {
        return 'No disponible';
      }

      const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

      return Number.isNaN(date.getTime())
        ? 'No disponible'
        : date.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
    },
  },

  created() {
    Event.$on('event-show-cancelBankAccountTransfer', this.openCancelModal);
  },

  beforeDestroy() {
    Event.$off('event-show-cancelBankAccountTransfer', this.openCancelModal);
  },
};
</script>

<style src="@/assets/css/bank-account-transfer-modals.css"></style>
